import { IconButton, TextField } from '@material-ui/core';
import { VisibilityIcon, VisibilityOffIcon } from 'annotation-tools/ui-parts';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';

const noop = () => undefined;

interface Props {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  onInput?: (v: string) => void;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  helperText?: string;
  error?: boolean;
}

export const MuiPasswordInput: FC<Props> = memo(
  ({
    label,
    value,
    onChange = noop,
    onInput = noop,
    fullWidth = true,
    margin,
    autoFocus,
    disabled,
    required,
    min,
    max,
    minLength,
    maxLength,
    helperText,
    error
  }: Readonly<Props>) => {
    const onChangeNative = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev.target.value ?? '');
      },
      [onChange]
    );

    const onInputNative = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        onInput(ev.target.value ?? '');
      },
      [onInput]
    );

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const toggleShowPassword = useCallback(() => {
      setShowPassword(b => !b);
    }, [setShowPassword]);

    const InputProps = useMemo(
      () => ({
        min,
        max,
        minLength,
        maxLength,
        endAdornment: (
          <IconButton
            aria-label='toggle password visibility'
            onClick={toggleShowPassword}
          >
            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        )
      }),
      [showPassword, toggleShowPassword, min, max, minLength, maxLength]
    );

    return (
      <TextField
        label={label}
        value={value}
        onChange={onChangeNative}
        onInput={onInputNative}
        autoFocus={autoFocus}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        margin={margin}
        type={showPassword ? 'text' : 'password'}
        variant='standard'
        InputProps={InputProps}
        helperText={helperText}
        error={error}
      />
    );
  }
);

MuiPasswordInput.displayName = 'MuiPasswordInput';
